// RigInspector — Unreal-style Material Slots panel for the character rig.
// One row per mesh primitive (the rig's "Element N" slots):
//
//   ┌────────┐  ┌────────────────────────┐
//   │ thumb  │  │ MaterialName       ▾ ✕ │       Element 0
//   │ sphere │  │ Slot: body_lod0_mesh   │
//   └────────┘  └────────────────────────┘
//
// Drop target accepts material asset cards; the dropdown field opens a
// filterable picker. Both call PUT /material-mapping and reload the avatar.

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import { assetRegistry } from '../../lib/assetRegistry'
import { downloadCharacterExport } from '../../lib/export/characterExport'
import { rigDownloadTarget, NO_RIG_REASON } from '../../lib/characterAssetUrl'
import * as aam from '../../lib/assetManagerApi'
import {
  rigActionAvailability, replaceWarning, isDeleteConfirmed,
  deleteConfirmationPhrase, rigVersionRows,
} from '../../lib/rigVersions'
import { carryOverSummary } from '../../lib/rigUpload'
import { materialThumbnailSrc } from '../../lib/materialThumbnail'
import { reloadMaterialsEverywhere } from '../../lib/materialReloadBus'
import { getCurrentCharacter, isSplatCharacter } from '../../lib/characterState'
import { SPLAT_SLOTS, resolveSplatMaterials, setSplatSlotMaterial, isGraphMaterialAsset } from '../../lib/splatMaterialBinding'
import AnchoredOverlay from '../AnchoredOverlay'
import { Z } from '../../lib/zLayers'

const C = {
  bg:       '#0c0e15',
  bgRow:    '#11131b',
  bgField:  '#080a10',
  border:   '#1f2129',
  borderHi: '#2a2c36',
  text:     '#e1e1ed',
  dim:      '#8f8fa2',
  accent:   '#8b6cff',
  drop:     '#4caf82',
}

export default function RigInspector({ asset, onNavigateToAsset }) {
  const [mapping, setMapping] = useState(asset.mapping ?? [])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [openSlotIndex, setOpenSlotIndex] = useState(null) // mesh index whose picker is open

  useEffect(() => { setMapping(asset.mapping ?? []); setError(null); setOpenSlotIndex(null) }, [asset.id])

  // ── Export npm package ────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false)
  const [exportMsg, setExportMsg] = useState(null)
  const [exportErr, setExportErr] = useState(null)
  const [exportProgress, setExportProgress] = useState(null)
  const lastPctRef = useRef(-1)

  const onExportProgress = ({ phase, loaded, total }) => {
    let label, pct
    if (phase === 'clips' && total > 0) {
      pct = Math.round((loaded / total) * 100)
      if (pct === lastPctRef.current) return
      lastPctRef.current = pct
      label = `Fetching clips… ${loaded} / ${total}`
    } else {
      lastPctRef.current = -1
      pct = null
      label = {
        blueprint:   'Fetching blueprint…',
        rig:         'Fetching rig GLB…',
        clips:       'Fetching animation clips…',
        materials:   'Fetching materials…',
        compressing: 'Compressing… (large files take a moment)',
        done:        'Finalizing…',
      }[phase] ?? 'Working…'
    }
    setExportProgress({ label, pct })
  }

  const doExport = async () => {
    setExporting(true); setExportErr(null); setExportMsg(null)
    setExportProgress({ label: 'Starting…', pct: null }); lastPctRef.current = -1
    try {
      const { fileName, packageName, states, clips } =
        await downloadCharacterExport(asset, { onProgress: onExportProgress })
      const stateNote = states.length
        ? `States: ${states.join(', ')}`
        : 'No states compiled.'
      setExportMsg(
        `Downloaded ${fileName}.\n${stateNote}\n` +
        `Install:\n  npm install ${packageName}\n` +
        `  import { AvatarCharacter } from '${packageName}'`
      )
    } catch (e) {
      setExportErr(String(e?.message ?? e))
    } finally {
      setExporting(false)
      setExportProgress(null)
    }
  }

  // ── Download rig GLB ───────────────────────────────────────────────────────
  // The exact GLB the runtime loads: fetch the credentialed proxy path (via
  // rigDownloadTarget → toProxiedAssetUrl), NOT the raw S3 URL, which 403s.
  const [downloadingRig, setDownloadingRig] = useState(false)
  const [rigDownloadErr, setRigDownloadErr] = useState(null)
  const rigTarget = useMemo(() => rigDownloadTarget(getCurrentCharacter()), [asset.id])

  const doDownloadRig = async () => {
    setDownloadingRig(true); setRigDownloadErr(null)
    try {
      const target = rigDownloadTarget(getCurrentCharacter())
      if (!target) throw new Error('This character has no rig GLB to download.')
      const res = await fetch(target.url)
      if (!res.ok) throw new Error(`rig fetch failed: ${res.status} ${res.statusText}`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = target.filename
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      setRigDownloadErr(String(e?.message ?? e))
    } finally {
      setDownloadingRig(false)
    }
  }

  // ── Replace / versions / permanent delete ─────────────────────────────────
  //
  // A rig upload is append-only server-side: it writes an immutable
  // rigs/{version}.glb and appends a history row. So Replace is not a
  // destructive act on the GEOMETRY — the previous rig stays restorable — but
  // it IS destructive to the material bindings, because the server re-derives
  // material_mapping from the new GLB on every upload. The confirmation says
  // so BEFORE the file goes up; finding out afterwards is how Vaelmor_v2's
  // eleven assignments were lost (2026-08-28) and had to be restored by hand.
  const replaceInputRef = useRef(null)
  const [rigBusy, setRigBusy] = useState(null)   // 'replace' | 'restore' | 'delete' | null
  const [rigErr, setRigErr] = useState(null)
  // What the last replace/restore KEPT, from the server's own count. Separate
  // from rigErr: it is not a failure, and it must not be styled as one.
  const [rigNote, setRigNote] = useState(null)
  const [versions, setVersions] = useState(null) // null = not loaded yet, [] = no history
  const [versionsErr, setVersionsErr] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteTyped, setDeleteTyped] = useState('')

  const charSlug = getCurrentCharacter()?.id ?? ''
  const currentRigUrl = getCurrentCharacter()?.rigPath ?? null
  const avail = rigActionAvailability({ hasRig: !!rigTarget, busy: !!rigBusy })
  const assignedCount = useMemo(() => mapping.filter(e => e.materialId).length, [mapping])

  // Load the history. A failure is SHOWN, not swallowed into an empty list —
  // "no versions" and "we could not ask" are different facts and the user acts
  // on them differently.
  const loadVersions = async () => {
    if (!charSlug) return
    setVersionsErr(null)
    try {
      setVersions(await aam.listRigVersions(charSlug))
    } catch (e) {
      setVersions(null)
      setVersionsErr(String(e?.message ?? e))
    }
  }
  useEffect(() => { loadVersions() }, [charSlug, currentRigUrl])

  // The server rebuilt material_mapping under us (upload and restore both
  // re-run the import), so the panel's copy is stale the moment either
  // returns. Re-read it rather than patching locally: guessing what the
  // importer produced is exactly the assumption that would show the user slot
  // names their new GLB does not have.
  const afterRigChange = async () => {
    await CustomAssetManager.refresh()
    setMapping(CustomAssetManager.listMaterialMapping())
    await loadVersions()
    reloadMaterialsEverywhere()
  }

  const doReplaceRig = async (file) => {
    if (!file) return
    if (!window.confirm(`Replace this character's rig with "${file.name}"?

${replaceWarning(assignedCount)}`)) return
    setRigBusy('replace'); setRigErr(null); setRigNote(null)
    // Read before the upload: afterRigChange() replaces `mapping` with the
    // re-derived one, so assignedCount is a different number by then and the
    // summary would compare the new mapping against itself.
    const assignedBefore = assignedCount
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await aam.uploadRig(charSlug, fd)
      await afterRigChange()
      setRigNote(carryOverSummary(res, assignedBefore))
    } catch (e) {
      setRigErr(String(e?.message ?? e))
    } finally {
      setRigBusy(null)
      if (replaceInputRef.current) replaceInputRef.current.value = ''
    }
  }

  const doRestore = async (versionId) => {
    setRigBusy('restore'); setRigErr(null); setRigNote(null)
    const assignedBefore = assignedCount
    try {
      const res = await aam.restoreRigVersion(charSlug, versionId)
      await afterRigChange()
      setRigNote(carryOverSummary(res, assignedBefore))
    } catch (e) {
      setRigErr(String(e?.message ?? e))
    } finally { setRigBusy(null) }
  }

  const doDeleteRig = async () => {
    setRigBusy('delete'); setRigErr(null); setRigNote(null)
    try {
      await aam.deleteRig(charSlug)
      setConfirmDelete(false); setDeleteTyped('')
      await afterRigChange()
    } catch (e) {
      setRigErr(String(e?.message ?? e))
    } finally { setRigBusy(null) }
  }

  const versionView = useMemo(
    () => rigVersionRows(versions ?? [], { currentUrl: currentRigUrl }),
    [versions, currentRigUrl])

  const matsById = useMemo(() => {
    const m = new Map()
    for (const row of CustomAssetManager.listMaterials()) m.set(row.id, row)
    return m
  }, [asset.id, CustomAssetManager.listMaterials().length])

  const texById = useMemo(() => {
    const m = new Map()
    for (const t of CustomAssetManager.listTextureAssets()) m.set(t.id, t)
    return m
  }, [asset.id, CustomAssetManager.listTextureAssets().length])

  // Splat characters have no mesh slots: bind a graph material to the
  // __splat__ / __hair__ sentinels instead of the per-element rows (Task 5,
  // SPLATS-MATERIAL.md). Detection reuses the canonical isSplatCharacter on the
  // cached active record — null (cache not ready) → false → mesh-slot path.
  const isSplat = useMemo(() => isSplatCharacter(getCurrentCharacter()), [asset.id])

  // Persist a full material_mapping array (shared by mesh-slot + sentinel
  // binds). __RELOAD_MATERIALS__ rebinds Myra's polygonal materials live; it's
  // absent in splat mode (AvatarModel isn't mounted), so the optional-call is a
  // no-op there — the splat shade picks the binding up on its next pass (Task 6).
  const persist = async (next) => {
    setMapping(next)
    setSaving(true)
    setError(null)
    try {
      await CustomAssetManager.putMaterialMapping(next)
      reloadMaterialsEverywhere()
    } catch (e) {
      setError(String(e.message ?? e))
    } finally {
      setSaving(false)
    }
  }

  const bind = (meshIndex, materialId) => persist(
    mapping.map(e => e.meshIndex === meshIndex
      ? (materialId ? { ...e, materialId } : (() => { const { materialId: _, ...rest } = e; return rest })())
      : e
    )
  )

  const bindSplat = (slotMeshName, materialId) => persist(setSplatSlotMaterial(mapping, slotMeshName, materialId))

  return (
    <>
      {/* Export npm package */}
      <div style={{ borderBottom: `1px solid ${C.border}`, background: '#080a10', padding: '8px 14px' }}>
        <div style={{
          fontFamily: 'var(--m-font)', fontSize: 9.5, letterSpacing: '.12em',
          textTransform: 'uppercase', color: C.dim, marginBottom: 6,
        }}>Export</div>
        <button
          onClick={doExport}
          disabled={exporting}
          data-testid="export-character-package"
          style={{
            width: '100%', textAlign: 'center',
            background: '#10180f', color: '#4caf82',
            border: '1px solid #4caf8255',
            borderRadius: 3, padding: '6px 8px',
            fontFamily: 'var(--m-font)', fontSize: 11,
            cursor: exporting ? 'wait' : 'pointer',
            opacity: exporting ? 0.6 : 1,
            marginBottom: exportProgress || exportMsg || exportErr ? 8 : 0,
          }}
          title="Export this character as a self-contained npm package (.tgz)"
        >
          {exporting ? '⏳  Building package…' : '📦  Export npm package'}
        </button>
        {/* Shown even when there is no rig to download: an unavailable control is
            listed and DISABLED with the reason inline, never hidden. */}
        <button
          onClick={doDownloadRig}
          disabled={downloadingRig || !rigTarget}
          data-testid="download-rig-glb"
          style={{
            width: '100%', textAlign: 'center',
            background: 'var(--m-panel-hi)', color: rigTarget ? 'var(--m-accent)' : C.dim,
            border: `1px solid ${rigTarget ? 'var(--m-accent-soft)' : C.border}`,
            borderRadius: 3, padding: '6px 8px',
            fontFamily: 'var(--m-font)', fontSize: 11,
            cursor: !rigTarget ? 'not-allowed' : (downloadingRig ? 'wait' : 'pointer'),
            opacity: (downloadingRig || !rigTarget) ? 0.6 : 1,
            marginTop: 6,
            marginBottom: (rigDownloadErr || !rigTarget) ? 8 : 0,
          }}
          title={rigTarget
            ? 'Download the rig GLB the app loads for this character'
            : NO_RIG_REASON}
        >
          {downloadingRig ? '⏳  Downloading…' : '⬇  Download rig GLB'}
        </button>
        {!rigTarget && (
          <div data-testid="download-rig-unavailable" style={{
            fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim, marginTop: 6,
          }}>{NO_RIG_REASON}</div>
        )}
        {rigDownloadErr && (
          <div data-testid="download-rig-error" style={{
            fontFamily: 'var(--m-font)', fontSize: 10, color: 'var(--m-danger)', marginTop: 6,
          }}>{rigDownloadErr}</div>
        )}

        {/* Replace. Shown and DISABLED with the reason inline when there is no
            rig, the same rule Download above follows. */}
        <input
          ref={replaceInputRef}
          type="file"
          accept=".glb,model/gltf-binary"
          data-testid="replace-rig-input"
          style={{ display: 'none' }}
          onChange={e => doReplaceRig(e.target.files?.[0])}
        />
        <button
          onClick={() => replaceInputRef.current?.click()}
          disabled={!avail.canReplace}
          data-testid="replace-rig-glb"
          title={avail.canReplace
            ? 'Upload a new rig GLB. The previous one is kept and stays restorable.'
            : avail.reason}
          style={{
            width: '100%', textAlign: 'center',
            background: 'var(--m-panel-hi)', color: avail.canReplace ? 'var(--m-accent)' : C.dim,
            border: `1px solid ${avail.canReplace ? 'var(--m-accent-soft)' : C.border}`,
            borderRadius: 3, padding: '6px 8px',
            fontFamily: 'var(--m-font)', fontSize: 11,
            cursor: avail.canReplace ? 'pointer' : 'not-allowed',
            opacity: avail.canReplace ? 1 : 0.6,
            marginTop: 6,
          }}
        >
          {rigBusy === 'replace' ? '⏳  Uploading…' : '⇪  Replace rig GLB'}
        </button>

        {rigErr && (
          <div data-testid="rig-action-error" style={{
            fontFamily: 'var(--m-font)', fontSize: 10, color: 'var(--m-danger)', marginTop: 6,
          }}>{rigErr}</div>
        )}

        {rigNote && (
          <div data-testid="rig-carryover-note" style={{
            fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim, marginTop: 6,
          }}>{rigNote}</div>
        )}
      </div>

      {/* Versions */}
      <div style={{ borderBottom: `1px solid ${C.border}`, background: C.bgField, padding: '8px 14px' }}>
        <div style={{
          fontFamily: 'var(--m-font)', fontSize: 9.5, letterSpacing: '.12em',
          textTransform: 'uppercase', color: C.dim, marginBottom: 6,
        }}>Versions</div>

        {versionsErr && (
          <div data-testid="rig-versions-error" style={{
            fontFamily: 'var(--m-font)', fontSize: 10, color: 'var(--m-danger)',
          }}>Could not load rig history: {versionsErr}</div>
        )}

        {/* "No history" and "we could not ask" are different facts, so only one
            of them is ever on screen. */}
        {!versionsErr && versionView.emptyReason && (
          <div data-testid="rig-versions-empty" style={{
            fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim,
          }}>{versionView.emptyReason}</div>
        )}

        {!versionsErr && versionView.rows.map(v => (
          <div
            key={v.row.id}
            data-testid="rig-version-row"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '4px 0', borderBottom: `1px solid ${C.border}`,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'var(--m-font)', fontSize: 10.5,
                color: v.isCurrent ? 'var(--m-accent)' : C.text,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{v.label}</div>
              {v.note && (
                <div style={{ fontFamily: 'var(--m-font)', fontSize: 9.5, color: C.dim }}>{v.note}</div>
              )}
            </div>
            {v.isCurrent
              ? <span data-testid="rig-version-current" style={{
                  fontFamily: 'var(--m-font)', fontSize: 9.5, color: 'var(--m-accent)',
                }}>current</span>
              : <button
                  onClick={() => doRestore(v.row.id)}
                  disabled={!!rigBusy}
                  data-testid="restore-rig-version"
                  title="Point the character back at this rig. Re-derives the material slots from it."
                  style={{
                    background: 'var(--m-panel-hi)', color: C.text,
                    border: `1px solid ${C.borderHi}`, borderRadius: 3,
                    padding: '2px 8px', fontFamily: 'var(--m-font)', fontSize: 10,
                    cursor: rigBusy ? 'wait' : 'pointer', opacity: rigBusy ? 0.6 : 1,
                  }}
                >Restore</button>}
          </div>
        ))}
      </div>

      {/* Danger zone */}
      <div style={{ borderBottom: `1px solid ${C.border}`, background: C.bgField, padding: '8px 14px' }}>
        <div style={{
          fontFamily: 'var(--m-font)', fontSize: 9.5, letterSpacing: '.12em',
          textTransform: 'uppercase', color: C.dim, marginBottom: 6,
        }}>Danger zone</div>

        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            disabled={!avail.canDelete}
            data-testid="delete-rig"
            title={avail.canDelete
              ? 'Permanently delete this rig and every stored version. No undo.'
              : avail.reason}
            style={{
              width: '100%', textAlign: 'center',
              background: 'var(--m-panel-hi)', color: avail.canDelete ? 'var(--m-danger)' : C.dim,
              border: `1px solid ${avail.canDelete ? 'var(--m-danger)' : C.border}`,
              borderRadius: 3, padding: '6px 8px',
              fontFamily: 'var(--m-font)', fontSize: 11,
              cursor: avail.canDelete ? 'pointer' : 'not-allowed',
              opacity: avail.canDelete ? 1 : 0.6,
            }}
          >Permanently delete rig</button>
        ) : (
          <div data-testid="delete-rig-confirm">
            <div style={{ fontFamily: 'var(--m-font)', fontSize: 10, color: C.text, marginBottom: 6 }}>
              This destroys the rig and every stored version, permanently. There is
              no undo and no backup. Materials and textures are kept. Type{' '}
              <code style={{ color: 'var(--m-danger)' }}>{deleteConfirmationPhrase(charSlug)}</code> to confirm.
            </div>
            <input
              value={deleteTyped}
              onChange={e => setDeleteTyped(e.target.value)}
              data-testid="delete-rig-input"
              placeholder={deleteConfirmationPhrase(charSlug)}
              style={{
                width: '100%', background: C.bgField, color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 3,
                padding: '4px 6px', fontFamily: 'var(--m-font)', fontSize: 11,
                marginBottom: 6, boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={doDeleteRig}
                disabled={!isDeleteConfirmed(deleteTyped, charSlug) || !!rigBusy}
                data-testid="delete-rig-confirm-button"
                style={{
                  flex: 1, background: 'var(--m-panel-hi)', color: 'var(--m-danger)',
                  border: '1px solid var(--m-danger)', borderRadius: 3,
                  padding: '5px 8px', fontFamily: 'var(--m-font)', fontSize: 11,
                  cursor: isDeleteConfirmed(deleteTyped, charSlug) && !rigBusy ? 'pointer' : 'not-allowed',
                  opacity: isDeleteConfirmed(deleteTyped, charSlug) && !rigBusy ? 1 : 0.5,
                }}
              >{rigBusy === 'delete' ? 'Deleting…' : 'Delete permanently'}</button>
              <button
                onClick={() => { setConfirmDelete(false); setDeleteTyped('') }}
                data-testid="delete-rig-cancel"
                style={{
                  flex: 1, background: 'var(--m-panel-hi)', color: C.text,
                  border: `1px solid ${C.borderHi}`, borderRadius: 3,
                  padding: '5px 8px', fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer',
                }}
              >Cancel</button>
            </div>
          </div>
        )}
        {exportProgress && (
          <div data-testid="export-progress" style={{ marginBottom: 8 }}>
            <div style={{ fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim, marginBottom: 3 }}>
              {exportProgress.label}
            </div>
            <div style={{ height: 6, background: '#080d07', border: '1px solid #4caf8233', borderRadius: 3, overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  background: '#4caf82',
                  width: exportProgress.pct != null ? `${exportProgress.pct}%` : '100%',
                  opacity: exportProgress.pct != null ? 1 : 0.4,
                  transition: 'width 0.15s linear',
                }}
              />
            </div>
          </div>
        )}
        {exportMsg && (
          <pre data-testid="export-success" style={{
            whiteSpace: 'pre-wrap', fontFamily: 'var(--m-mono)', fontSize: 10,
            color: '#4caf82', background: '#080d07', border: '1px solid #4caf8233',
            borderRadius: 3, padding: 6, marginBottom: 0, marginTop: 0,
          }}>{exportMsg}</pre>
        )}
        {exportErr && (
          <div data-testid="export-error" style={{
            fontFamily: 'var(--m-font)', fontSize: 10, color: C.drop === '#4caf82' ? '#f87171' : '#f87171',
            background: '#1a0d0d', border: '1px solid #f8717133',
            borderRadius: 3, padding: 6,
          }}>Export failed: {exportErr}</div>
        )}
      </div>

      <div style={{
        padding: '8px 14px', fontFamily: 'var(--m-font)', fontSize: 9.5,
        letterSpacing: '.12em', textTransform: 'uppercase', color: C.dim,
        background: '#080a10', borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span>{isSplat ? 'Splat Material' : 'Material Slots'}</span>
        <span style={{ color: C.text }}>{isSplat ? SPLAT_SLOTS.length : mapping.length}</span>
      </div>
      {(saving || error) && (
        <div style={{
          padding: '4px 14px', fontFamily: 'var(--m-font)', fontSize: 10,
          color: error ? '#f87171' : C.dim,
          borderBottom: `1px solid ${C.border}`,
        }}>
          {error ? `⚠ ${error}` : 'Saving…'}
        </div>
      )}
      {isSplat ? (
        <div style={{ background: C.bg }}>
          <div style={{
            padding: '6px 14px', fontFamily: 'var(--m-font)', fontSize: 9.5,
            color: C.dim, borderBottom: `1px solid ${C.border}`, lineHeight: 1.5,
          }}>
            Bind a graph material to each splat. Only node-graph materials shade splats.
          </div>
          {SPLAT_SLOTS.map((slot) => {
            const entry = mapping.find(e => e && e.meshName === slot.meshName) ?? { meshName: slot.meshName }
            return (
              <SlotRow
                key={slot.meshName}
                entry={entry}
                label={slot.label}
                graphOnly
                matsById={matsById}
                material={entry.materialId ? matsById.get(entry.materialId) : null}
                texById={texById}
                onBind={(id) => bindSplat(slot.meshName, id)}
                isOpen={openSlotIndex === slot.meshName}
                onToggleOpen={() => setOpenSlotIndex(prev => prev === slot.meshName ? null : slot.meshName)}
                onClosePicker={() => setOpenSlotIndex(null)}
                onNavigateToAsset={onNavigateToAsset}
              />
            )
          })}
        </div>
      ) : (
        <div style={{ background: C.bg }}>
          {mapping.map((entry, i) => (
            <SlotRow
              key={`${entry.meshIndex}_${i}`}
              entry={entry}
              element={i}
              material={entry.materialId ? matsById.get(entry.materialId) : null}
              texById={texById}
              onBind={(id) => bind(entry.meshIndex, id)}
              isOpen={openSlotIndex === entry.meshIndex}
              onToggleOpen={() => setOpenSlotIndex(prev => prev === entry.meshIndex ? null : entry.meshIndex)}
              onClosePicker={() => setOpenSlotIndex(null)}
              onNavigateToAsset={onNavigateToAsset}
            />
          ))}
          {mapping.length === 0 && (
            <div style={{
              padding: 14, fontFamily: 'var(--m-font)', fontSize: 10,
              color: C.dim, textAlign: 'center',
            }}>
              No material slots on this rig.
            </div>
          )}
        </div>
      )}
    </>
  )
}

// `label` overrides the "Element N" caption (splat sentinel slots show
// "Splat appearance" / "Hair"). `graphOnly` (with `matsById`) restricts binding
// to node-graph materials — splats shade through the compiled graph, so a
// PBR/toon material dropped on a splat slot is rejected rather than mis-bound.
function SlotRow({ entry, element, label, graphOnly, matsById, material, texById, onBind, isOpen, onToggleOpen, onClosePicker, onNavigateToAsset }) {
  const [hover, setHover] = useState(false)
  const rowRef = useRef(null)
  const fieldRef = useRef(null)

  const bindable = (id) => !graphOnly || (!!matsById && isGraphMaterialAsset(matsById.get(id)))

  const onDragOver = (e) => {
    if (!e.dataTransfer.types.includes('text/aos-asset-id')) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'link'
    setHover(true)
  }
  const onDragLeave = () => setHover(false)
  const onDrop = (e) => {
    setHover(false)
    const id = e.dataTransfer.getData('text/aos-asset-id')
    if (!id || !id.startsWith('material:')) return
    e.preventDefault()
    const rawId = id.slice('material:'.length)
    if (!bindable(rawId)) return // splat slot + non-graph material → reject
    onBind(rawId)
  }

  const hidden = !!entry.hidden

  const lastSelected = assetRegistry.getLastSelected('material')
  const lastSelectedBindable = !!lastSelected && bindable(lastSelected)
  const useSelected = () => {
    if (lastSelectedBindable) onBind(lastSelected)
  }

  return (
    <div
      ref={rowRef}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 14px',
        borderBottom: `1px solid ${C.border}`,
        background: hover ? '#1f2233' : 'transparent',
        opacity: hidden ? 0.5 : 1,
      }}
    >
      <div
        onClick={() => !hidden && onToggleOpen()}
        style={{ cursor: hidden ? 'default' : 'pointer' }}
        title={hidden ? 'hidden' : 'Click to pick a material'}
      >
        <SlotThumbnail material={material} texById={texById} hidden={hidden} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--m-font)', fontSize: 9, letterSpacing: '.1em',
          textTransform: 'uppercase', color: C.dim, marginBottom: 3,
        }}>
          {label ?? `Element ${element}`}{hidden ? ' — hidden' : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div
            ref={fieldRef}
            onClick={() => !hidden && onToggleOpen()}
            style={{
              flex: 1, minWidth: 0,
              display: 'flex', alignItems: 'center', gap: 6,
              background: C.bgField,
              border: `1px solid ${hover ? C.drop : C.borderHi}`,
              borderRadius: 3, padding: '4px 8px',
              cursor: hidden ? 'default' : 'pointer',
            }}
          >
            <span style={{
              flex: 1, fontFamily: 'var(--m-font)', fontSize: 11,
              color: material ? C.text : C.dim,
              fontStyle: material ? 'normal' : 'italic',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {material?.name ?? (hidden ? 'hidden' : 'none')}
            </span>
            {!hidden && <span style={{ color: C.dim, fontSize: 10 }}>▾</span>}
          </div>
          {!hidden && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); useSelected() }}
                disabled={!lastSelectedBindable}
                title={lastSelectedBindable
                  ? 'Use last-selected material in browser'
                  : (graphOnly ? 'Select a graph material in the content browser first' : 'Select a material in the content browser first')}
                style={iconBtn(!lastSelectedBindable)}
              >←</button>
              <button
                onClick={(e) => { e.stopPropagation(); if (material && onNavigateToAsset) onNavigateToAsset(`material:${material.id}`) }}
                disabled={!material || !onNavigateToAsset}
                title="Find in content browser"
                style={iconBtn(!material || !onNavigateToAsset)}
              >🔍</button>
              {material && (
                <button
                  onClick={(e) => { e.stopPropagation(); onBind(null) }}
                  title="Unbind"
                  style={iconBtn(false)}
                >↺</button>
              )}
            </>
          )}
        </div>
        <div style={{
          fontFamily: 'var(--m-mono)', fontSize: 9.5, color: C.dim,
          marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={entry.meshName}>
          Slot: {entry.meshName ?? '(unnamed)'}
        </div>
      </div>
      {isOpen && (
        <MaterialPicker
          anchorRef={fieldRef}
          materials={graphOnly
            ? CustomAssetManager.listMaterials().filter(isGraphMaterialAsset)
            : CustomAssetManager.listMaterials()}
          texById={texById}
          current={entry.materialId}
          onPick={(id) => { onBind(id); onClosePicker() }}
          onClose={onClosePicker}
        />
      )}
    </div>
  )
}

function iconBtn(disabled) {
  return {
    background: 'transparent',
    border: `1px solid ${disabled ? '#1f2129' : '#2a2c36'}`,
    color: disabled ? '#3a3c45' : '#8f8fa2',
    fontFamily: 'var(--m-font)', fontSize: 10,
    width: 22, height: 22, borderRadius: 3,
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, padding: 0,
  }
}

// Compact PlayCanvas/Unreal-style material thumbnail: applies the material's
// diffuse texture (or tint) to a sphere with a radial highlight overlay.
function SlotThumbnail({ material, texById, hidden }) {
  const SIZE = 52
  if (hidden) {
    return (
      <div style={{
        width: SIZE, height: SIZE, borderRadius: 3, flexShrink: 0,
        background: `repeating-linear-gradient(45deg, #2a2c36 0 6px, #14161e 6px 12px)`,
        border: `1px solid ${C.border}`,
      }} />
    )
  }
  if (!material) {
    return (
      <div style={{
        width: SIZE, height: SIZE, borderRadius: 3, flexShrink: 0,
        background: `repeating-conic-gradient(#1d1f28 0 25%, #14161e 0 50%) 50% / 14px 14px`,
        border: `1px solid ${C.border}`,
      }} />
    )
  }
  // Prefer the real-render snapshot captured at graph-commit time — the only
  // faithful preview for a graph material (whose look lives in data.graph, not
  // data.diffuse). Without this, graph materials fell back to the flat '#888'
  // tint below, so the slot showed a gray sphere while the content-browser
  // card showed the real render (AssetIcon uses the same snapshot).
  const snapshot = materialThumbnailSrc(material)
  if (snapshot) {
    return (
      <div style={{
        width: SIZE, height: SIZE, borderRadius: 3, flexShrink: 0,
        border: `1px solid ${C.border}`, overflow: 'hidden',
        background: `repeating-conic-gradient(#1d1f28 0 25%, #14161e 0 50%) 50% / 14px 14px`,
      }}>
        <img src={snapshot} alt="" draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
    )
  }
  const d = material.data ?? {}
  const diffuse = d.diffuse
  const tint = Array.isArray(diffuse) && diffuse.length === 3
    ? `rgb(${Math.round(diffuse[0] * 255)},${Math.round(diffuse[1] * 255)},${Math.round(diffuse[2] * 255)})`
    : '#888'
  const texRow = d.diffuseMap ? texById.get(d.diffuseMap) : null
  const baseLayer = texRow?.file_url
    ? `url(${texRow.file_url}) center / cover`
    : tint
  const shading = `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0) 22%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.7) 100%)`
  return (
    <div style={{
      width: SIZE, height: SIZE, borderRadius: 3, flexShrink: 0,
      background: `repeating-conic-gradient(#1d1f28 0 25%, #14161e 0 50%) 50% / 14px 14px`,
      border: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative',
    }}>
      <div style={{
        width: Math.round(SIZE * 0.78), height: Math.round(SIZE * 0.78),
        borderRadius: '50%', background: baseLayer, position: 'relative', overflow: 'hidden',
        boxShadow: `inset -3px -3px 6px rgba(0,0,0,0.55)`,
      }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: shading, mixBlendMode: 'overlay',
        }} />
      </div>
    </div>
  )
}

function MaterialPicker({ anchorRef, materials, texById, current, onPick, onClose }) {
  const [filter, setFilter] = useState('')
  const popupRef = useRef(null)
  useEffect(() => {
    const onDocClick = (e) => {
      // Picker is portaled outside the anchor (position: fixed) so a click on
      // a row would otherwise unmount the popup on mousedown before the
      // click's onClick reaches it. Check the popup too.
      if (anchorRef.current?.contains(e.target)) return
      if (popupRef.current?.contains(e.target)) return
      onClose()
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [anchorRef, onClose])

  const filtered = materials.filter(m => !filter || m.name.toLowerCase().includes(filter.toLowerCase()))
  // Anchored under the field. getBoundingClientRect reports ZOOMED pixels and
  // a `position: fixed` left is multiplied by the zoom again, so writing
  // rect.left straight in double-scaled the offset (measured: 100px paints at
  // 110 at the default UI scale). AnchoredOverlay converts through the popup's
  // OWN cumulative zoom — which is what matters here and not the anchor's: the
  // sibling TextureSlotField popup portals to <body> while its anchor can be
  // inside a ReactFlow node, and those two have different net zooms.
  //
  // The WIDTH comes from offsetWidth, not rect.width, for the same reason:
  // offsetWidth is a layout metric in the element's own units (measured: 200 vs
  // rect's 220 under zoom 1.1), so it needs no conversion.
  const rect = anchorRef.current?.getBoundingClientRect()
  const anchorW = anchorRef.current?.offsetWidth ?? 0

  return (
    <AnchoredOverlay
      x={rect?.left ?? 0} y={(rect?.bottom ?? 0) + 4}
      innerRef={popupRef}
      style={{
        width: Math.max(240, anchorW),
        zIndex: Z.POPOVER,
        background: '#1a1d28', border: `1px solid ${C.border}`,
        borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,.45)',
        maxHeight: 320, overflowY: 'auto', padding: 4,
      }}
    >
      <input
        autoFocus
        placeholder="Filter…"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        style={{
          width: '100%', boxSizing: 'border-box',
          background: C.bgField, border: `1px solid ${C.border}`,
          borderRadius: 3, padding: '4px 6px',
          color: C.text, fontFamily: 'var(--m-font)', fontSize: 10,
          outline: 'none', marginBottom: 4,
        }}
      />
      <PickerRow current={!current} onClick={() => onPick(null)}>
        <div style={{
          width: 28, height: 28, borderRadius: 3,
          background: `repeating-conic-gradient(#1d1f28 0 25%, #14161e 0 50%) 50% / 8px 8px`,
        }} />
        <span style={{ color: C.dim, fontStyle: 'italic', fontFamily: 'var(--m-font)', fontSize: 10 }}>None</span>
      </PickerRow>
      {filtered.map(m => (
        <PickerRow key={m.id} current={m.id === current} onClick={() => onPick(m.id)}>
          <SlotThumbnail material={m} texById={texById} />
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{
              fontFamily: 'var(--m-font)', fontSize: 11, color: C.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{m.name}</div>
            <div style={{
              fontFamily: 'var(--m-font)', fontSize: 9, letterSpacing: '.1em',
              textTransform: 'uppercase', color: C.dim,
            }}>{m.type}</div>
          </div>
        </PickerRow>
      ))}
      {filtered.length === 0 && (
        <div style={{
          padding: 8, fontFamily: 'var(--m-font)', fontSize: 10,
          color: C.dim, textAlign: 'center',
        }}>
          No materials match.
        </div>
      )}
    </AnchoredOverlay>
  )
}

function PickerRow({ current, onClick, children }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', textAlign: 'left',
        background: current ? '#252938' : (hover ? '#1f2233' : 'transparent'),
        border: 'none', padding: 4, borderRadius: 3,
        cursor: 'pointer', color: C.text,
      }}
    >{children}</button>
  )
}

